import React from 'react';
import type { TFunction } from 'i18next';
import { Button } from '@ooh/ui/components/button';
import { RowPreviewPanel } from '@ooh/ui/components/row-preview-panel';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@ooh/ui/components/table';
import type { Client } from '@ooh/contracts';

export function ClientsEmptyState({ t }: { t: TFunction<'clients'> }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
      <p className="font-medium">{t('empty.title')}</p>
      <p className="text-sm mt-1">{t('empty.description')}</p>
    </div>
  );
}

interface ClientsTableProps {
  clients: Client[];
  canUpdate: boolean;
  canDelete: boolean;
  activeClientId: Client['id'] | undefined;
  selectedClientId: Client['id'] | null;
  onEdit: (client: Client) => void;
  onArchive: (client: Client) => void;
  onSelectClient: (client: Client) => void;
  t: TFunction<'clients'>;
}

export function ClientsTable({
  clients,
  canUpdate,
  canDelete,
  activeClientId,
  selectedClientId,
  onEdit,
  onArchive,
  onSelectClient,
  t,
}: ClientsTableProps) {
  const selectedClient = clients.find((client) => client.id === selectedClientId) ?? null;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead caps={false}>{t('table.name')}</TableHead>
          <TableHead caps={false} aria-label={t('table.actions')} />
        </TableRow>
      </TableHeader>
      <TableBody>
        {clients.map((client) => (
          <React.Fragment key={client.id}>
            <TableRow
              listRow
              data-testid={`client-row-${String(client.id)}`}
              // eslint-disable-next-line i18next/no-literal-string
              data-active={activeClientId === client.id ? 'true' : undefined}
              onClick={() => onSelectClient(client)}
            >
              <TableCell className="py-2 px-3 font-medium">{client.name}</TableCell>
              <TableCell className="py-2 px-3">
                <div className="flex items-center gap-2 justify-end">
                  {canUpdate ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`editButton-${String(client.id)}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onEdit(client);
                      }}
                    >
                      {t('actions.edit')}
                    </Button>
                  ) : null}
                  {canDelete ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`archiveButton-${String(client.id)}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onArchive(client);
                      }}
                    >
                      {t('actions.archive')}
                    </Button>
                  ) : null}
                </div>
              </TableCell>
            </TableRow>
            {selectedClientId === client.id && selectedClient ? (
              <RowPreviewPanel
                colSpan={2}
                columns={[
                  { key: 'notes', label: t('details.notes') },
                  { key: 'brands', label: t('details.brands') },
                ]}
                emptyLabel={t('details.emptyValue')}
                label={t('details.title')}
                row={{
                  brands: selectedClient.brands.join(', '),
                  notes: selectedClient.notes ?? '',
                }}
                testId="client-details-panel"
              />
            ) : null}
          </React.Fragment>
        ))}
      </TableBody>
    </Table>
  );
}
